import React, { useState, useEffect, useCallback } from 'react';
import Input from '../../../components/bootstrap/forms/Input';
import Textarea from '../../../components/bootstrap/forms/Textarea';
import Button from '../../../components/bootstrap/Button';
import RichTextInput from '../../../components/bootstrap/forms/RichTextInput';
import { uploadFile, getFileUrl } from '../../../helpers/axios';
import api from '../../../helpers/axios';
import { PostType } from '../../../types/postTypes';
import { usePostTypes, usePostTypeValidation } from '../../../hooks/usePostTypes';
import PostTypeSelector from '../../../components/PostTypeSelector/PostTypeSelector';
import EnhancedFileUpload from '../../../components/EnhancedFileUpload/EnhancedFileUpload';
import MediaRenderer from '../../../components/MediaRenderer';
import AIAssistant from '../../../components/AIAssistant/AIAssistant';
import Card, { CardBody, CardHeader } from '../../../components/bootstrap/Card';
import { StructuredContentResponse } from '../../../types/aiTypes';

interface SubArticleModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSave: (data: any) => void;
  initialData: any;
  tagsList: string[];
  parentPostType?: PostType;
  allowPostTypeSelection?: boolean;
  enableAIAssistant?: boolean; // New prop to enable AI assistant in sub-article modal
}

const SubArticleModal = ({
  isOpen,
  setIsOpen,
  onSave,
  initialData,
  tagsList,
  parentPostType,
  allowPostTypeSelection = true,
  enableAIAssistant = true, // Default to true to enable AI features
}: SubArticleModalProps) => {
  const [form, setForm] = useState(initialData);
  const [useSameAsMainContent, setUseSameAsMainContent] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const { postTypeList } = usePostTypes();
  const validation = usePostTypeValidation();

  useEffect(() => {
    console.log('SubArticleModal - useEffect triggered');
    console.log('initialData:', initialData);
    console.log('parentPostType:', parentPostType);
    
    const enhancedInitialData = {
      ...initialData,
      imagePath: initialData.imagePath || '',
      thumbnailPath: initialData.thumbnailPath || '',
      fileUrl: initialData.fileUrl || initialData.imagePath || '', // Support both fields
      postType: initialData.postType || parentPostType || PostType.NORMAL_POST,
      // Initialize visibility control fields with defaults if not present
      showTitle: initialData.showTitle !== false, // Default to true
      showThumbnail: initialData.showThumbnail !== false, // Default to true
      // Initialize advertisement redirect fields
      redirectUrl: initialData.redirectUrl || '',
      isAdvertisement: initialData.isAdvertisement || false,
    };

    console.log('Setting form to:', enhancedInitialData);
    setForm(enhancedInitialData);
  }, [initialData, parentPostType]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, files } = e.target as HTMLInputElement;

    setForm((f: any) => ({
      ...f,
      [name]: files ? files[0] : value,
    }));
  };

  const handlePostTypeChange = (postType: PostType) => {
    console.log('SubArticleModal - received postType:', postType);
    
    setForm((prevForm: any) => {
      const newForm = {
        ...prevForm,
        postType: postType, // Use the value directly
      };
      console.log('SubArticleModal - updating form from:', prevForm.postType, 'to:', postType);
      return newForm;
    });
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f: any) => ({
      ...f,
      tags: e.target.value.split(',').map((t: string) => t.trim()),
    }));
  };

  const handleEnhancedFileUpload = (uploadedPath: string, fileType: 'main' | 'thumbnail') => {
    if (fileType === 'main') {
      setForm((f: any) => ({
        ...f,
        fileUrl: uploadedPath,
        imagePath: uploadedPath, // Maintain backward compatibility
      }));
      
      // If "use same as main content" is checked, also set thumbnail
      if (useSameAsMainContent) {
        setForm((f: any) => ({
          ...f,
          fileUrl: uploadedPath,
          imagePath: uploadedPath, // Maintain backward compatibility
          thumbnailPath: uploadedPath,
        }));
      }
    } else {
      setForm((f: any) => ({
        ...f,
        thumbnailPath: uploadedPath,
      }));
    }
  };

  // Handle checkbox change for "use same as main content"
  const handleUseSameAsMainContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setUseSameAsMainContent(isChecked);
    
    // If checked and we have a main file, copy it to thumbnail
    if (isChecked && (form.fileUrl || form.imagePath)) {
      const mainFileUrl = form.fileUrl || form.imagePath;
      setForm((f: any) => ({
        ...f,
        thumbnailPath: mainFileUrl,
      }));
    } else if (!isChecked) {
      // If unchecked, clear the thumbnail (user can upload a different one)
      setForm((f: any) => ({
        ...f,
        thumbnailPath: '',
      }));
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target as HTMLInputElement;
    if (!files || files.length === 0) return;

    try {
      // Enhanced upload with post-type support
      const uploadedPath = await uploadFile(files[0], form.postType, name === 'image' ? 'file' : 'thumbnail');
      
      if (name === 'image') {
        setForm((f: any) => ({
          ...f,
          fileUrl: uploadedPath,
          imagePath: uploadedPath, // Maintain backward compatibility
        }));
      } else {
        setForm((f: any) => ({
          ...f,
          [`${name}Path`]: uploadedPath,
        }));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleRemoveFile = async (fileType: 'imagePath' | 'thumbnailPath' | 'fileUrl') => {
    try {
      const filePath = form[fileType];
      if (filePath) {
        await api.delete(`/api/files/delete?filePath=${encodeURIComponent(filePath)}`);
        
        if (fileType === 'imagePath' || fileType === 'fileUrl') {
          setForm((f: any) => ({
            ...f,
            imagePath: '',
            fileUrl: '',
          }));
        } else {
          setForm((f: any) => ({
            ...f,
            [fileType]: '',
          }));
        }
      }
    } catch (error) {
      console.error('Error removing file:', error);
    }
  };

  // ================== AI ASSISTANT HANDLERS ==================

  /**
   * Handle AI content generation callback (for logging/tracking)
   */
  const handleAIContentGenerated = useCallback((content: StructuredContentResponse) => {
    console.log('SubArticle AI Content Generated:', content);
  }, []);

  /**
   * Handle AI content selection - apply generated content to sub-article form
   * Includes audioUrl support for TTS-generated audio narration
   */
  const handleAIContentSelected = useCallback((content: { title: string; description: string; body: string; audioUrl?: string }) => {
    setForm((f: any) => ({
      ...f,
      title: content.title || f.title,
      description: content.description || f.description,
      body: content.body || f.body,
      audioUrl: content.audioUrl || f.audioUrl, // Store AI-generated audio URL
    }));
    
    // Close AI assistant panel after applying content
    setShowAIAssistant(false);
  }, []);

  /**
   * Toggle AI Assistant visibility
   */
  const toggleAIAssistant = useCallback(() => {
    setShowAIAssistant(prev => !prev);
  }, []);

  /**
   * Handle removing audio from sub-article
   */
  const handleRemoveAudio = useCallback(() => {
    setForm((f: any) => ({
      ...f,
      audioUrl: '',
    }));
  }, []);

  const handleSave = () => {
    const payload = {
      ...form,
      image: form.fileUrl || form.imagePath, // Use unified field with fallback
      thumbnail: form.thumbnailPath,
      postType: form.postType,
      audioUrl: form.audioUrl || undefined, // Include TTS-generated audio URL
      // Mobile visibility control fields
      showTitle: form.showTitle !== false, // Default to true if not explicitly set to false
      showThumbnail: form.showThumbnail !== false, // Default to true if not explicitly set to false
      // Advertisement redirect support for sub-articles
      redirectUrl: form.redirectUrl || undefined, // External URL for advertisement sub-articles
      isAdvertisement: form.isAdvertisement || false, // Flag indicating sub-article is an advertisement
      // Include post-type specific metadata
      metadata: {
        postType: form.postType,
        hasFile: !!(form.fileUrl || form.imagePath),
        hasThumbnail: !!form.thumbnailPath,
        hasAudio: !!form.audioUrl,
        tagCount: form.tags?.length || 0,
        showTitle: form.showTitle !== false,
        showThumbnail: form.showThumbnail !== false,
        isAdvertisement: form.isAdvertisement || false,
        hasRedirectUrl: !!form.redirectUrl,
      },
    };

    onSave(payload); // Pass the enhanced payload back to the parent component
    setIsOpen(false); // Close the modal
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-backdrop" 
      style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        backgroundColor: 'rgba(0,0,0,0.5)', 
        zIndex: 1050,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '1rem',
        overflowY: 'auto'
      }}
    >
      <div
        className="modal-content p-4"
        style={{
          width: showAIAssistant ? '95%' : '800px',
          maxWidth: showAIAssistant ? '1400px' : '900px',
          margin: '1rem auto',
          maxHeight: 'calc(100vh - 2rem)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {/* Header with AI Assistant Toggle */}
        <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
          <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
            📄 Sub-Article Editor
          </h5>
          <div className="d-flex gap-2 align-items-center">
            {enableAIAssistant && (
              <Button
                color={showAIAssistant ? 'secondary' : 'primary'}
                size="sm"
                onClick={toggleAIAssistant}
                className="d-flex align-items-center gap-2"
              >
                <i className={`fas fa-robot ${showAIAssistant ? '' : 'fa-bounce'}`} style={{ fontSize: '1rem' }}></i>
                {showAIAssistant ? 'Hide AI Assistant' : 'AI Assistant'}
              </Button>
            )}
            <Button color="light" size="sm" onClick={() => setIsOpen(false)}>
              <i className="fas fa-times"></i>
            </Button>
          </div>
        </div>

        {/* Main Content Area - Responsive Layout */}
        <div 
          className="row g-3" 
          style={{ 
            flex: 1, 
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 180px)'
          }}
        >
          {/* AI Assistant Panel - Collapsible */}
          {showAIAssistant && enableAIAssistant && (
            <div className={showAIAssistant ? 'col-lg-5 col-md-6' : 'd-none'}>
              <Card className="h-100 shadow-sm border-primary" style={{ borderWidth: '2px' }}>
                <CardHeader className="bg-primary text-white py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">
                      <i className="fas fa-magic me-2"></i>
                      AI Content Assistant
                    </span>
                    <small className="opacity-75">Generate sub-article content</small>
                  </div>
                </CardHeader>
                <CardBody style={{ padding: 0, overflow: 'auto', maxHeight: '60vh' }}>
                  <AIAssistant
                    onContentGenerated={handleAIContentGenerated}
                    onContentSelected={handleAIContentSelected}
                    currentContent={{
                      title: form.title || '',
                      description: form.description || '',
                      body: form.body || '',
                      postType: form.postType || undefined
                    }}
                    className="sub-article-ai-assistant"
                  />
                </CardBody>
              </Card>
            </div>
          )}

          {/* Sub-Article Form */}
          <div className={showAIAssistant ? 'col-lg-7 col-md-6' : 'col-12'}>
            <div 
              className="card shadow-sm h-100" 
              style={{ 
                display: 'flex', 
                flexDirection: 'column',
                border: '1px solid #e9ecef'
              }}
            >
              <div
                className="card-body"
                style={{
                  overflowY: 'auto',
                  flex: 1,
                  paddingBottom: '1rem',
                }}
              >
                {/* Post Type Selector */}
                {allowPostTypeSelection && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Post Type</label>
                    <div className="mb-2">
                      <small className="text-info">Current: {String(form.postType || 'None')}</small>
                    </div>
                    <PostTypeSelector
                      selectedPostType={form.postType}
                      onPostTypeChange={handlePostTypeChange}
                      showAutoDetect={false}
                      size="sm"
                    />
                    {parentPostType && (
                      <small className="text-muted">
                        Parent article type: {parentPostType.replace('_', ' ').toLowerCase()}
                      </small>
                    )}
                  </div>
                )}

                {/* Title Field */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Title <span className="text-danger">*</span>
                  </label>
                  <RichTextInput
                    name="title"
                    value={form.title}
                    onChange={(value) => {
                      setForm((f: any) => ({ ...f, title: value }));
                    }}
                    placeholder="Enter sub-article title"
                    fieldType="title"
                  />
                </div>

                {/* Description Field */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Description</label>
                  <RichTextInput
                    name="description"
                    value={form.description}
                    onChange={(value) => {
                      setForm((f: any) => ({ ...f, description: value }));
                    }}
                    placeholder="Enter brief description"
                    fieldType="description"
                  />
                </div>

                {/* Body Field */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Body <span className="text-danger">*</span>
                  </label>
                  <RichTextInput
                    name="body"
                    value={form.body}
                    onChange={(value) => {
                      setForm((f: any) => ({ ...f, body: value }));
                    }}
                    placeholder="Enter sub-article body content"
                    fieldType="body"
                  />
                </div>

                {/* TTS Audio Preview Section */}
                {form.audioUrl && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-volume-up text-success me-2"></i>
                      Generated Audio (TTS)
                    </label>
                    <div className="p-3 bg-light rounded border border-success" style={{ borderWidth: '2px' }}>
                      <div className="d-flex align-items-center mb-2">
                        <span className="badge bg-success me-2">
                          <i className="fas fa-check-circle me-1"></i>
                          Audio Ready
                        </span>
                        <small className="text-muted">AI-generated narration</small>
                      </div>
                      <audio 
                        controls 
                        style={{ width: '100%', height: '40px' }}
                        src={form.audioUrl}
                        preload="metadata"
                      >
                        Your browser does not support the audio element.
                      </audio>
                      <div className="mt-2 d-flex justify-content-between align-items-center">
                        <small className="text-muted text-truncate" style={{ maxWidth: '70%' }}>
                          <i className="fas fa-link me-1"></i>
                          {form.audioUrl.split('/').pop()}
                        </small>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={handleRemoveAudio}
                        >
                          <i className="fas fa-trash me-1"></i>
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enhanced File Upload Section */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">File Content</label>
                  {(form.fileUrl || form.imagePath) && (
                    <div className="mb-2 p-3 bg-light rounded">
                      <div className="text-center">
                        <MediaRenderer
                          fileUrl={form.fileUrl || form.imagePath}
                          postType={form.postType}
                          alt="File Content"
                          style={{ maxWidth: '100%', maxHeight: 250 }}
                          className="rounded mb-2"
                          onError={(e) => {
                            console.error('Failed to load file:', form.fileUrl || form.imagePath);
                          }}
                        />
                      </div>
                      <div className="text-center">
                        <Button color="danger" size="sm" onClick={() => handleRemoveFile('fileUrl')}>
                          <i className="fas fa-trash me-1"></i> Remove File
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <EnhancedFileUpload
                    key={`file-upload-${form.postType}`}
                    instanceId={`sub-article-main-content-${Date.now()}`}
                    postType={form.postType}
                    fileCategory="file"
                    onFileUploaded={(path) => handleEnhancedFileUpload(path, 'main')}
                    showValidation={true}
                    allowExternalUrl={true}
                    placeholder={`Upload ${form.postType === PostType.VIDEO_POST ? 'video' : form.postType === PostType.PODCAST_POST ? 'audio' : form.postType === PostType.IMAGE_POST ? 'image' : 'file'} content`}
                    size="sm"
                  />
                </div>

                {/* Thumbnail Upload Section */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Thumbnail Image</label>
                  
                  <div className="mb-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="useSameAsMainContentSub"
                        checked={useSameAsMainContent}
                        onChange={handleUseSameAsMainContentChange}
                      />
                      <label className="form-check-label small" htmlFor="useSameAsMainContentSub">
                        Use same as main content/file
                      </label>
                    </div>
                  </div>
                  
                  {form.thumbnailPath && (
                    <div className="mb-2 p-3 bg-light rounded">
                      <div className="text-center">
                        <MediaRenderer
                          fileUrl={form.thumbnailPath}
                          postType={PostType.IMAGE_POST}
                          alt="Thumbnail Image"
                          style={{ maxWidth: '100%', maxHeight: 150 }}
                          className="rounded mb-2"
                          onError={(e) => {
                            console.error('Failed to load thumbnail:', form.thumbnailPath);
                          }}
                        />
                      </div>
                      <div className="text-center">
                        <Button color="danger" size="sm" onClick={() => handleRemoveFile('thumbnailPath')}>
                          <i className="fas fa-trash me-1"></i> Remove Thumbnail
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {!useSameAsMainContent && (
                    <EnhancedFileUpload
                      key="sub-article-thumbnail-upload"
                      instanceId={`sub-article-thumbnail-${Date.now()}`}
                      postType={PostType.IMAGE_POST}
                      fileCategory="thumbnail"
                      onFileUploaded={(path) => handleEnhancedFileUpload(path, 'thumbnail')}
                      showValidation={true}
                      allowExternalUrl={true}
                      placeholder="Upload thumbnail image"
                      size="sm"
                    />
                  )}
                  
                  {useSameAsMainContent && !form.thumbnailPath && !form.fileUrl && !form.imagePath && (
                    <div className="text-muted small">
                      <i className="fas fa-info-circle me-2"></i>
                      Upload main content first to use it as thumbnail
                    </div>
                  )}
                </div>

                {/* Tags Section */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Tags</label>
                  <Input
                    name="tags"
                    value={form.tags?.join(',') || ''}
                    onChange={handleTagsChange}
                    placeholder="Enter tags (comma separated)"
                  />
                  <small className="text-muted">Separate multiple tags with commas</small>
                </div>

                {/* Mobile Display Visibility Controls */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-mobile-alt me-2 text-primary"></i>
                    Mobile Display Options
                  </label>
                  <div className="p-3 bg-light rounded border">
                    <small className="text-muted d-block mb-2">
                      Control what elements are visible when this sub-article is displayed in the mobile app
                    </small>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="showTitleCheckbox"
                        checked={form.showTitle !== false}
                        onChange={(e) => setForm((f: any) => ({ ...f, showTitle: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="showTitleCheckbox">
                        <i className="fas fa-heading me-2 text-secondary"></i>
                        Show Title
                      </label>
                    </div>
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="showThumbnailCheckbox"
                        checked={form.showThumbnail !== false}
                        onChange={(e) => setForm((f: any) => ({ ...f, showThumbnail: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="showThumbnailCheckbox">
                        <i className="fas fa-image me-2 text-secondary"></i>
                        Show Thumbnail/Image
                      </label>
                    </div>
                    {form.showTitle === false && form.showThumbnail === false && (
                      <div className="alert alert-info small py-2 mb-0 mt-2">
                        <i className="fas fa-info-circle me-2"></i>
                        Share and Favorite buttons will be hidden when both title and thumbnail are disabled.
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Sponsored Content / Advertisement Configuration - Available for ALL post types */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-ad me-2 text-warning"></i>
                    Sponsored Content / Advertisement
                  </label>
                  <div className="p-3 bg-warning bg-opacity-10 rounded border border-warning">
                    <small className="text-muted d-block mb-2">
                      <i className="fas fa-info-circle me-1"></i>
                      Mark this sub-article as sponsored content to enable click-through redirection.
                    </small>
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isAdvertisementCheckbox"
                        checked={form.isAdvertisement || false}
                        onChange={(e) => setForm((f: any) => ({ ...f, isAdvertisement: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="isAdvertisementCheckbox">
                        <strong>This is a Sponsored Sub-Article</strong>
                        <small className="text-muted d-block">
                          When enabled, tapping this sub-article will redirect users to the external URL below
                        </small>
                      </label>
                    </div>
                    
                    {form.isAdvertisement && (
                      <div className="mb-3">
                        <label className="form-label">Redirect URL <span className="text-danger">*</span></label>
                        <Input
                          type="url"
                          name="redirectUrl"
                          placeholder="https://example.com/landing-page"
                          value={form.redirectUrl || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                            setForm((f: any) => ({ ...f, redirectUrl: e.target.value }))
                          }
                          className={
                            form.redirectUrl && 
                            !form.redirectUrl.match(/^https?:\/\/[^\s]+$/)
                              ? 'is-invalid'
                              : ''
                          }
                        />
                        {form.redirectUrl && !form.redirectUrl.match(/^https?:\/\/[^\s]+$/) && (
                          <div className="invalid-feedback d-block">
                            Please enter a valid URL starting with http:// or https://
                          </div>
                        )}
                        {form.isAdvertisement && !form.redirectUrl && (
                          <div className="text-warning small mt-1">
                            <i className="fas fa-exclamation-triangle me-1"></i>
                            Redirect URL is required for sponsored content
                          </div>
                        )}
                        <small className="text-muted d-block mt-1">
                          <i className="fas fa-external-link me-1"></i>
                          This URL will open in the user's browser when they tap this sub-article
                        </small>
                      </div>
                    )}
                    
                    {/* Compliance Notice - Only when advertisement is enabled */}
                    {form.isAdvertisement && (
                      <div className="alert alert-warning small py-2 mb-0">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Advertising Policy:</strong> Ensure redirect URLs comply with advertising policies.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="d-flex gap-2 justify-content-between align-items-center p-3 border-top bg-light rounded-bottom">
                <div className="text-muted small">
                  {form.title && form.body ? (
                    <span className="text-success">
                      <i className="fas fa-check-circle me-1"></i>
                      Ready to save
                    </span>
                  ) : (
                    <span>
                      <i className="fas fa-exclamation-circle me-1"></i>
                      Title and body are required
                    </span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button color="secondary" onClick={() => setIsOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    color="success" 
                    onClick={handleSave}
                    isDisable={!form.title || !form.body}
                  >
                    <i className="fas fa-save me-1"></i> Save Sub-Article
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubArticleModal;